import { useCallback } from 'react';
import { ProjectNode } from '@/shared/types/projectData';
import { normalizeTaskStateUpdate } from '@/shared/utils/taskStateSync';
import { getPriorityHexColor } from '@/shared/utils/priorityMetadata';
import {
  getMilestoneScheduleDate,
  getTaskScheduleEnd,
} from '@/shared/utils/nodeSchedule';

export type TaskStatus = 'todo' | 'in-progress' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

export interface TaskUpdate {
  status?: TaskStatus;
  priority?: TaskPriority;
  assignees?: string[];
  progress?: number;
  startDate?: string;
  endDate?: string;
  description?: string;
}

export const useTaskManagement = () => {
  const getRelevantScheduleDate = useCallback((node: ProjectNode): Date | null => {
    const rawValue = node.type === 'milestone'
      ? getMilestoneScheduleDate(node)
      : getTaskScheduleEnd(node);
    return rawValue ? new Date(rawValue) : null;
  }, []);

  // Get task status based on progress and dates
  const deriveStatus = useCallback((node: ProjectNode): TaskStatus => {
    const progress = node.progress || 0;
    const now = new Date();
    const endDate = getRelevantScheduleDate(node);

    if (progress >= 100) return 'done';
    if (progress > 0) return 'in-progress';
    if (node.status === 'done') return 'done';
    if (node.status === 'in-progress' || node.completionState === 'in-progress') return 'in-progress';
    if (endDate && endDate < now) return 'in-progress'; // Overdue
    return 'todo';
  }, [getRelevantScheduleDate]);

  // Check if task is overdue
  const isOverdue = useCallback((node: ProjectNode): boolean => {
    const endDate = getRelevantScheduleDate(node);
    return endDate ? endDate < new Date() && deriveStatus(node) !== 'done' : false;
  }, [deriveStatus, getRelevantScheduleDate]);

  // Check if task is due soon (within specified days)
  const isDueSoon = useCallback((node: ProjectNode, daysAhead: number = 7): boolean => {
    const now = new Date();
    const threshold = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));
    const endDate = getRelevantScheduleDate(node);
    return endDate ? endDate <= threshold && endDate >= now && deriveStatus(node) !== 'done' : false;
  }, [deriveStatus, getRelevantScheduleDate]);

  // Get task duration in days
  const getDurationDays = useCallback((node: ProjectNode): number => {
    if (!node.startDate || !node.endDate) return 0;
    
    const start = new Date(node.startDate);
    const end = new Date(node.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, []);

  // Get priority color
  const getPriorityColor = useCallback((priority?: TaskPriority): string => {
    return getPriorityHexColor(priority);
  }, []);

  // Get status color
  const getStatusColor = useCallback((status?: TaskStatus): string => {
    switch (status) {
      case 'done': return '#059669'; // emerald-600
      case 'in-progress': return '#2563eb'; // blue-600
      case 'todo': return '#6b7280'; // gray-500
      default: return '#6b7280';
    }
  }, []);

  // Check if user can edit task based on role, assignment, and view context
  const canEditTask = useCallback((
    node: ProjectNode, 
    currentUser: { id: string; role: string; username: string } | null,
    hasPermission: (resource: string, action: string, view?: string | null) => boolean,
    viewContext?: string | null
  ): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    
    // Check view-specific permission first if view context is provided
    if (!hasPermission('tasks', 'update', viewContext)) return false;

    // Check if user is assigned to the task
    if (node.assignees?.includes(currentUser.username) || 
        node.assignee === currentUser.username) {
      return true;
    }

    // For clients, only allow editing their own assigned tasks
    if (currentUser.role === 'client') {
      return false;
    }

    // Employees can edit unassigned tasks or tasks they created
    return true;
  }, []);

  // Validate task data
  const validateTask = useCallback((taskData: Partial<ProjectNode>): string[] => {
    const errors: string[] = [];

    if (!taskData.title?.trim()) {
      errors.push('Task title is required');
    }

    if (taskData.startDate && taskData.endDate) {
      const start = new Date(taskData.startDate);
      const end = new Date(taskData.endDate);
      if (start > end) {
        errors.push('Start date must be before end date');
      }
    }

    if (taskData.progress !== undefined) {
      const progress = Number(taskData.progress);
      if (isNaN(progress) || progress < 0 || progress > 100) {
        errors.push('Progress must be between 0 and 100');
      }
    }

    if (taskData.assignees && !Array.isArray(taskData.assignees)) {
      errors.push('Assignees must be an array');
    }

    return errors;
  }, []);

  // Sort tasks by priority and due date
  const sortTasks = useCallback((
    tasks: ProjectNode[], 
    sortBy: 'priority' | 'dueDate' | 'status' | 'title' = 'dueDate',
    ascending: boolean = true
  ): ProjectNode[] => {
    const sorted = [...tasks].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'priority': {
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          const aPriority = priorityOrder[a.priority as TaskPriority] || 0;
          const bPriority = priorityOrder[b.priority as TaskPriority] || 0;
          comparison = aPriority - bPriority;
          break;
        }

        case 'dueDate': {
          const aDate = a.type === 'milestone' ? getMilestoneScheduleDate(a) : getTaskScheduleEnd(a);
          const bDate = b.type === 'milestone' ? getMilestoneScheduleDate(b) : getTaskScheduleEnd(b);
          if (!aDate && !bDate) comparison = 0;
          else if (!aDate) comparison = 1;
          else if (!bDate) comparison = -1;
          else comparison = new Date(aDate).getTime() - new Date(bDate).getTime();
          break;
        }

        case 'status': {
          const statusOrder = { todo: 1, 'in-progress': 2, done: 3 };
          const aStatus = statusOrder[deriveStatus(a)] || 0;
          const bStatus = statusOrder[deriveStatus(b)] || 0;
          comparison = aStatus - bStatus;
          break;
        }

        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
      }

      return ascending ? comparison : -comparison;
    });

    return sorted;
  }, [deriveStatus]);

  // Filter tasks by various criteria
  const filterTasks = useCallback((
    tasks: ProjectNode[],
    criteria: {
      status?: TaskStatus[];
      priority?: TaskPriority[];
      assignee?: string;
      overdue?: boolean;
      dueSoon?: boolean;
      search?: string;
    }
  ): ProjectNode[] => {
    return tasks.filter(task => {
      // Status filter
      if (criteria.status && !criteria.status.includes(deriveStatus(task))) {
        return false;
      }

      // Priority filter
      if (criteria.priority && task.priority && !criteria.priority.includes(task.priority as TaskPriority)) {
        return false;
      }

      // Assignee filter
      if (criteria.assignee) {
        const isAssigned = task.assignees?.includes(criteria.assignee) || 
                          task.assignee === criteria.assignee;
        if (!isAssigned) return false;
      }

      // Overdue filter
      if (criteria.overdue && !isOverdue(task)) {
        return false;
      }

      // Due soon filter
      if (criteria.dueSoon && !isDueSoon(task)) {
        return false;
      }

      // Search filter
      if (criteria.search) {
        const searchTerm = criteria.search.toLowerCase();
        const matchesTitle = task.title.toLowerCase().includes(searchTerm);
        const matchesDescription = task.description?.toLowerCase().includes(searchTerm);
        const matchesAssignees = task.assignees?.some(assignee => 
          assignee.toLowerCase().includes(searchTerm)
        );
        
        if (!matchesTitle && !matchesDescription && !matchesAssignees) {
          return false;
        }
      }

      return true;
    });
  }, [deriveStatus, isOverdue, isDueSoon]);

  // Create task from template with defaults
  const createTaskFromTemplate = useCallback((overrides: Partial<ProjectNode> = {}): Partial<ProjectNode> => {
    const defaults: Partial<ProjectNode> = {
      type: 'task',
      status: 'todo',
      priority: 'medium',
      progress: 0,
      assignees: [],
      tags: [],
      description: ''
    };

    const mergedTask = { ...defaults, ...overrides };
    return normalizeTaskStateUpdate(mergedTask, mergedTask);
  }, []);

  return {
    deriveStatus,
    isOverdue,
    isDueSoon,
    getDurationDays,
    getPriorityColor,
    getStatusColor,
    canEditTask,
    validateTask,
    sortTasks,
    filterTasks,
    createTaskFromTemplate
  };
};
